/**
 * Token Storage Utility
 * 
 * Provides cross-platform token storage using Capacitor Preferences on iOS/Android
 * and localStorage on web. This ensures tokens persist reliably across app restarts.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

interface TokenData {
  idToken: string;
  refreshToken: string;
  userId: string;
  tokenExpiry: number; // Timestamp in milliseconds
}

const TOKEN_KEYS = {
  ID_TOKEN: 'firebase_idToken',
  REFRESH_TOKEN: 'firebase_refreshToken',
  USER_ID: 'firebase_userId',
  TOKEN_EXPIRY: 'firebase_tokenExpiry',
} as const;

/**
 * Check if we should use Capacitor Preferences (iOS/Android) or localStorage (web)
 */
function shouldUseCapacitorStorage(): boolean {
  return Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
}

/**
 * Save authentication tokens to persistent storage
 */
export async function saveTokens(
  idToken: string,
  refreshToken: string,
  userId: string
): Promise<void> {
  // Calculate token expiry (idToken expires in 1 hour)
  const tokenExpiry = Date.now() + 3600 * 1000; // Current time + 1 hour in milliseconds

  if (shouldUseCapacitorStorage()) {
    // Use Capacitor Preferences for iOS/Android
    try {
      await Preferences.set({ key: TOKEN_KEYS.ID_TOKEN, value: idToken });
      await Preferences.set({ key: TOKEN_KEYS.REFRESH_TOKEN, value: refreshToken });
      await Preferences.set({ key: TOKEN_KEYS.USER_ID, value: userId });
      await Preferences.set({ key: TOKEN_KEYS.TOKEN_EXPIRY, value: tokenExpiry.toString() });
      console.log('[TokenStorage] Tokens saved to Capacitor Preferences');
    } catch (error) {
      console.error('[TokenStorage] Failed to save tokens to Capacitor Preferences:', error);
      throw error;
    }
  } else {
    // Use localStorage for web
    try {
      localStorage.setItem(TOKEN_KEYS.ID_TOKEN, idToken);
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
      localStorage.setItem(TOKEN_KEYS.USER_ID, userId);
      localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, tokenExpiry.toString());
      console.log('[TokenStorage] Tokens saved to localStorage');
    } catch (error) {
      console.error('[TokenStorage] Failed to save tokens to localStorage:', error);
      throw error;
    }
  }
}

/**
 * Get stored authentication tokens
 */
export async function getTokens(): Promise<TokenData | null> {
  try {
    let idToken: string | null;
    let refreshToken: string | null;
    let userId: string | null;
    let tokenExpiryStr: string | null;

    if (shouldUseCapacitorStorage()) {
      // Use Capacitor Preferences for iOS/Android
      const idTokenResult = await Preferences.get({ key: TOKEN_KEYS.ID_TOKEN });
      const refreshTokenResult = await Preferences.get({ key: TOKEN_KEYS.REFRESH_TOKEN });
      const userIdResult = await Preferences.get({ key: TOKEN_KEYS.USER_ID });
      const expiryResult = await Preferences.get({ key: TOKEN_KEYS.TOKEN_EXPIRY });

      idToken = idTokenResult.value;
      refreshToken = refreshTokenResult.value;
      userId = userIdResult.value;
      tokenExpiryStr = expiryResult.value;
    } else {
      // Use localStorage for web
      idToken = localStorage.getItem(TOKEN_KEYS.ID_TOKEN);
      refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
      userId = localStorage.getItem(TOKEN_KEYS.USER_ID);
      tokenExpiryStr = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
    }

    // Check if all required tokens exist
    if (!idToken || !refreshToken || !userId || !tokenExpiryStr) {
      console.log('[TokenStorage] No tokens found in storage');
      return null;
    }

    const tokenExpiry = parseInt(tokenExpiryStr, 10);
    if (isNaN(tokenExpiry)) {
      console.warn('[TokenStorage] Invalid token expiry, clearing tokens');
      await clearTokens();
      return null;
    }

    return {
      idToken,
      refreshToken,
      userId,
      tokenExpiry,
    };
  } catch (error) {
    console.error('[TokenStorage] Failed to get tokens:', error);
    return null;
  }
}

/**
 * Check if tokens exist in storage
 */
export async function hasTokens(): Promise<boolean> {
  const tokens = await getTokens();
  return tokens !== null;
}

/**
 * Check if the stored idToken is expired
 */
export async function isTokenExpired(): Promise<boolean> {
  const tokens = await getTokens();
  if (!tokens) {
    return true; // No tokens = expired
  }

  // Add 5 minute buffer (refresh before actual expiry)
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  const now = Date.now();
  return now >= (tokens.tokenExpiry - bufferTime);
}

/**
 * Get the stored idToken (synchronous for compatibility with existing code)
 * Note: This is a convenience function that uses async internally but returns synchronously
 * For new code, prefer using getTokens() which is fully async
 */
export function getIdTokenSync(): string | null {
  if (shouldUseCapacitorStorage()) {
    // For Capacitor, we can't do sync access, so return null
    // Callers should use getTokens() instead
    console.warn('[TokenStorage] getIdTokenSync() called on Capacitor platform - use getTokens() instead');
    return null;
  }

  try {
    return localStorage.getItem(TOKEN_KEYS.ID_TOKEN);
  } catch (error) {
    console.warn('[TokenStorage] Could not get idToken from localStorage:', error);
    return null;
  }
}

/**
 * Clear all stored authentication tokens
 */
export async function clearTokens(): Promise<void> {
  if (shouldUseCapacitorStorage()) {
    // Use Capacitor Preferences for iOS/Android
    try {
      await Preferences.remove({ key: TOKEN_KEYS.ID_TOKEN });
      await Preferences.remove({ key: TOKEN_KEYS.REFRESH_TOKEN });
      await Preferences.remove({ key: TOKEN_KEYS.USER_ID });
      await Preferences.remove({ key: TOKEN_KEYS.TOKEN_EXPIRY });
      console.log('[TokenStorage] Tokens cleared from Capacitor Preferences');
    } catch (error) {
      console.error('[TokenStorage] Failed to clear tokens from Capacitor Preferences:', error);
    }
  } else {
    // Use localStorage for web
    try {
      localStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.USER_ID);
      localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
      console.log('[TokenStorage] Tokens cleared from localStorage');
    } catch (error) {
      console.error('[TokenStorage] Failed to clear tokens from localStorage:', error);
    }
  }
}

/**
 * Update stored tokens (useful after token refresh)
 */
export async function updateTokens(
  idToken: string,
  refreshToken?: string
): Promise<void> {
  const tokens = await getTokens();
  if (!tokens) {
    throw new Error('No existing tokens to update');
  }

  // Calculate new expiry
  const tokenExpiry = Date.now() + 3600 * 1000; // Current time + 1 hour

  if (shouldUseCapacitorStorage()) {
    // Use Capacitor Preferences for iOS/Android
    await Preferences.set({ key: TOKEN_KEYS.ID_TOKEN, value: idToken });
    if (refreshToken) {
      await Preferences.set({ key: TOKEN_KEYS.REFRESH_TOKEN, value: refreshToken });
    }
    await Preferences.set({ key: TOKEN_KEYS.TOKEN_EXPIRY, value: tokenExpiry.toString() });
  } else {
    // Use localStorage for web
    localStorage.setItem(TOKEN_KEYS.ID_TOKEN, idToken);
    if (refreshToken) {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    }
    localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, tokenExpiry.toString());
  }
}

